import React, { useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DataGrid, GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';

import { ResponseBucket, ResponseData } from 'components/widgets/utility/CategoryChartsAPIResponseHandlers';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from 'system/widget/Widget';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { Query } from 'system/query/Query';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import textColumn from 'components/tables/columns/TextColumn';
import distributionBarColumn from 'components/tables/columns/DistributionBarColumn';
import percentColumn from 'components/tables/columns/PercentColumn';
import numberColumn from 'components/tables/columns/NumberColumn';
import percentChangeColumn from 'components/tables/columns/PercentChangeColumn';
import { ColumnModel, ColumnModelItem, ColumnRenderType } from 'components/tables/columns/ColumnModel';
import { dataTableDefaultSettings } from './dataTableUtils';
import { calculatePercentChange } from 'components/widgets/charts/PercentChange/PercentChange';
import { toTitleCase } from 'components/formatting/TitleCase';
import StripedDataGrid from 'components/uiElements/dataGrid/StripedDataGrid';

export const initialColumnModel: ColumnModel = {
  columns: [
    {
      id: "_count",
      field: "_count",
      headerName: "records",
      fieldType: "integer",
      sourceField: "_count",
      renderType: ColumnRenderType.Number
    },
    {
      id: "percent",
      field: "percent",
      headerName: "percent",
      fieldType: "integer",
      sourceField: "percent",
      renderType: ColumnRenderType.Percent
    },
  ],
};

function DataTableContent(props: WithQueryWidgetProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'data-table');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'data-table');
  const globalDataResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'data-table-global');
  const percentChangeResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'data-table-percent-change');

  const apiResponse = apiResponseInfo?.responseBody || {};
  const responseKey = Object.keys(apiResponse).find(key => key !== "_total") || "";
  const responseData: ResponseData = apiResponse[responseKey] || {};
  const responseName = responseData?._field_label;
  const originalAggsField = originalRequest?.requestInit?.body.aggs[responseName]?.field ?? "";
  const buckets = responseData?._buckets || [];
  const currentState = {...dataTableDefaultSettings, ...props.widget?.data?.settings};
  const [selectedRow, setSelectedRow] = useState<null | ResponseBucket>();

  const renderCellType = (renderType: ColumnRenderType) => {
    switch (renderType) {
      case "number":
        return numberColumn();
      case "distributionBar":
        return distributionBarColumn();
      case "percent":
        return percentColumn();
      case "percent_change":
        return percentChangeColumn();
      default:
        return undefined;
    }
  };

  const defaultRenderColumns: GridColDef[] = initialColumnModel.columns.map((colItem: ColumnModelItem) => {
    return {
      ...colItem,
      headerName: colItem.headerName === "percent" ?
        i18n.t("percent-of-total") :
        i18n.t(colItem.headerName),
      disableColumnMenu: true,
      ...(colItem.renderType && renderCellType(colItem.renderType)),
    };
  });

  const renderColumns = useMemo(() => {
    const labelColumn = {
      field: '_label',
      ...textColumn(),
      headerName: `${responseName}`,
      disableColumnMenu: true,
    };

    const modelColumns: GridColDef[] = props.widget?.data?.columnModel?.columns?.map((colItem: ColumnModelItem) => {
      return {
        ...colItem,
        field: colItem.field,
        headerName: colItem.headerName,
        width: colItem.width ?? 111,
        align: colItem.align ? colItem.align : "right",
        headerAlign: colItem.headerAlign ?? colItem.align ?? "right",
        disableColumnMenu: true,
        ...((colItem && colItem.renderType) && (
          renderCellType(colItem.renderType)
        )),
        valueGetter: (value: number | undefined, row: GridValidRowModel, col: GridColDef) => {
          if (colItem.sourceField === "ratio" && row.global_data) {
            return (row.percent / row.global_data?.[row._key]).toFixed(2) + "x";
          }

          if (colItem.sourceField === "percent_change") {
            return row.percent_change_data;
          }

          return row[col.field.split("-")[0]];
        }
      };
    });

    return [labelColumn, ...(modelColumns || defaultRenderColumns)];
  }, [props.widget?.data?.columnModel?.columns]);

  // Converts ColumnModel format to MUI's DataGrid format
  const modelToMUIInitialState = (): GridInitialStateCommunity => {
    const modelColumns = props.widget?.data?.columnModel?.columns;
    const orderedFields = [
      "_label",
      ...(modelColumns?.map((item: ColumnModelItem) => item.field) ?? ["_count", "percent"])
    ];

    return {
      columns: {
        orderedFields
      }
    };
  };

  const rows = buckets.map((bucket: ResponseBucket, index: number) => {
    return {
      ...bucket,
      id: index,
      _label: toTitleCase(bucket._label),
      ...(globalDataResponseInfo?.responseBody && {
        global_data: globalDataResponseInfo?.responseBody
      }),
      ...(percentChangeResponseInfo?.responseBody && (
        () => {
          const series = percentChangeResponseInfo?.responseBody?.[responseData._field_id]?.series || [];
          const seriesFieldData = series?.find((s: Record<string, string | number[]>) => s.key === bucket._key);

          return {
            percent_change_data: calculatePercentChange(seriesFieldData?.values)
          };
        }
      )()),
    };
  });

  return (
    <>
      {buckets.length !== 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState?.widgetTitleAlignment}
              customTitle={currentState?.customWidgetTitle}
              defaultTitle={i18n.t("widget-types.data-table.label")}
              settingsTitle={currentState?.widgetTitle ?? ""}
              style={{ pb: 2 }}
              widgetQueryTitle={apiResponseInfo?.queryDisplayTitle}
            />
          }
        >
          <Box sx={{width: "100%", height: "100%"}}>
            <StripedDataGrid
              showRowStripes={currentState.rowStripes}
              initialState={modelToMUIInitialState()}
              columns={renderColumns}
              density={currentState?.density ?? "standard"}
              disableColumnMenu
              rows={rows}
              onRowClick={(rowData) => setSelectedRow(rowData.row)}
            />
          </Box>
        </WidgetLayout>
      }
      {buckets.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
      {selectedRow &&
        <DashboardQueryDrilldownDialog
          title={selectedRow._label ?? ""}
          queryInfo={props.queryInfo}
          onClose={() => setSelectedRow(null)}
          activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
          drilldownQuery={new Query(
            {
              query: {
                op: "contains",
                value: selectedRow._key,
                fields: [originalAggsField]
              }
            }
          )}
          widgetRequestInfo={props.widget?.request_info}
        />
      }
    </>
  );
}

export default withQueryWidget(DataTableContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
